import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./useAuth";
import { useTenant } from "./useTenant";
import { RBACService } from "@/services/rbacService";
import type { Permission, Role, PermissionName } from "@/types/rbac";

// Export query key for prefetching
export const permissionsKeys = {
  byUserId: (userId: string, tenantId: string) =>
    ["permissions", userId, tenantId] as const,
};

interface UsePermissionsReturn {
  permissions: Permission[];
  roles: Role[];
  hasPermission: (permissionName: PermissionName) => boolean;
  hasAnyPermission: (permissionNames: PermissionName[]) => boolean;
  hasAllPermissions: (permissionNames: PermissionName[]) => boolean;
  hasRole: (roleName: string) => boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: permissionsKeys.byUserId(user?.id || "", tenantId || ""),
    queryFn: async () => {
      if (!user?.id) {
        return { permissions: [], roles: [] };
      }
      const userWithRoles = await RBACService.getUserWithRoles(
        user.id,
        tenantId || undefined
      );
      return {
        permissions: userWithRoles.permissions,
        roles: userWithRoles.roles,
      };
    },
    enabled: !!user?.id && !!tenantId,
    staleTime: 60000, // Cache permissions for 1 minute
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  const permissions = data?.permissions || [];
  const roles = data?.roles || [];

  const hasPermission = useCallback(
    (permissionName: PermissionName): boolean => {
      return permissions.some((p) => p.name === permissionName);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (permissionNames: PermissionName[]): boolean => {
      return permissionNames.some((name) =>
        permissions.some((p) => p.name === name)
      );
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (permissionNames: PermissionName[]): boolean => {
      return permissionNames.every((name) =>
        permissions.some((p) => p.name === name)
      );
    },
    [permissions]
  );

  const hasRole = useCallback(
    (roleName: string): boolean => {
      return roles.some((r) => r.name === roleName);
    },
    [roles]
  );

  const refetch = useCallback(async () => {
    if (user?.id) {
      await queryClient.invalidateQueries({
        queryKey: permissionsKeys.byUserId(user.id, tenantId || ""),
      });
    }
  }, [queryClient, user?.id, tenantId]);

  return {
    permissions,
    roles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isLoading,
    error: error as Error | null,
    refetch,
  };
};
